// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 07-source-control-pipeline.md
import * as React from 'react';
import { CreditCard } from 'lucide-react';

import type { ActionsBlocked, CurrentUser } from '@/lib/auth';

interface ActionsBlockedBannerProps {
  blocked: ActionsBlocked;
  role: CurrentUser['role'];
}

const ADMIN_ROLES: ReadonlyArray<CurrentUser['role']> = ['org_admin', 'admin'];

/**
 * Dashboard-wide banner for IT while GitHub refuses to start the org's workflow jobs for
 * billing. Builders learn it from their run's check, which says the fix belongs to IT.
 *
 * Not dismissable: every pipeline in the org is blocked until it clears, and it clears by
 * itself on the next run whose jobs start.
 */
export function ActionsBlockedBanner({
  blocked,
  role,
}: ActionsBlockedBannerProps): React.JSX.Element | null {
  if (!ADMIN_ROLES.includes(role)) return null;
  const billingUrl = `https://github.com/organizations/${encodeURIComponent(blocked.scm_org_handle)}/settings/billing`;

  return (
    <div
      role="region"
      aria-label="GitHub Actions status"
      data-testid="actions-blocked-banner"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-[hsl(var(--status-red))] bg-[hsl(var(--status-red)/0.15)] px-4 py-2 pt-[calc(0.5rem+env(safe-area-inset-top))] md:px-6"
    >
      <CreditCard className="size-4 shrink-0 text-[hsl(var(--status-red-text))]" aria-hidden />
      <p className="min-w-0 flex-1 text-[13px] text-foreground">
        <span className="font-semibold">
          GitHub has stopped running pipelines for {blocked.scm_org_handle} because of a billing
          problem.
        </span>{' '}
        <span className="text-muted-foreground">
          No app can pass its checks or deploy until an owner of the GitHub organization fixes the
          payment method or spending limit. Builders are told the fix is yours.
        </span>
      </p>
      <a
        href={billingUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex h-11 shrink-0 items-center text-[13px] font-medium text-link underline-offset-4 hover:underline md:h-9"
      >
        Open GitHub billing
      </a>
    </div>
  );
}
